import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PortfolioSelector from './PortfolioSelector';
import { selectionFromParams, prefilled } from '../lib/reportPrefill';
import { defaultReportDate, maxReportDate } from '../lib/reportDates';
import MaskedDateField from '../../../components/ui/MaskedDateField';

/**
 * Fixed Income Distribution Confirmation Popup
 *
 * Thin shell around the shared <PortfolioSelector> (groups vs accounts, @@
 * aggregate, routing by item type — not the legacy startsWith('@') heuristic)
 * plus the report date, printed-header inputs, and the report options.
 */
const FixedIncomeDistributionPopup = ({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  reportInfo,
  initialParams = null,
  darkMode = false
}) => {
  const [selection, setSelection] = useState({ selectedItem: null, params: {} });
  const [date, setDate] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Printed report-header inputs (firm name, client/account name, account code).
  // The PDF prints these at the top of every page; the Athena query never
  // depends on them, they just flow through to the rendered output.
  const [firmName, setFirmName] = useState('Banyan Tree Asset Management, LLC');
  const [accountName, setAccountName] = useState('');
  const [accountCode, setAccountCode] = useState('');

  // Report options (all checked by default)
  const [showSummaryInformation, setShowSummaryInformation] = useState(true);
  const [showDistributionByMoodyRating, setShowDistributionByMoodyRating] = useState(true);
  const [showDistributionBySpRating, setShowDistributionBySpRating] = useState(true);
  // Maturity section defaults OFF: APX prints it only for select templates
  // (e.g. KCRIMCO), so it is opt-in rather than a standing default.
  const [showDistributionByMaturity, setShowDistributionByMaturity] = useState(false);

  // Set default date to yesterday when the popup opens.
  useEffect(() => {
    if (isOpen) {
      setDate(prefilled(initialParams, 'date', defaultReportDate()));
      setErrors({});
      setSelection({ selectedItem: null, params: {} });
      setFirmName(prefilled(initialParams, 'firm_name', 'Banyan Tree Asset Management, LLC'));
      setAccountName(prefilled(initialParams, 'account_name', ''));
      setAccountCode(prefilled(initialParams, 'account_code', ''));
      // Rating sections default ON, maturity OFF - mirror buildReportRequestParams.
      setShowSummaryInformation(prefilled(initialParams, 'show_summary_information', true));
      setShowDistributionByMoodyRating(prefilled(initialParams, 'show_distribution_by_moody_rating', true));
      setShowDistributionBySpRating(prefilled(initialParams, 'show_distribution_by_sp_rating', true));
      setShowDistributionByMaturity(prefilled(initialParams, 'show_distribution_by_maturity', false));
    }
  }, [isOpen, initialParams]);

  // The single thin-params builder used by handleConfirm.
  const buildParams = (sel) => ({
    ...sel.params,
    date: date,
    firm_name: firmName,
    account_name: accountName,
    account_code: accountCode,
    show_summary_information: showSummaryInformation,
    show_distribution_by_moody_rating: showDistributionByMoodyRating,
    show_distribution_by_sp_rating: showDistributionBySpRating,
    show_distribution_by_maturity: showDistributionByMaturity
  });

  // Auto-fill the printed account header from the chosen item so the user can
  // override it but doesn't have to type it from scratch.
  const handleSelectionChange = (next) => {
    setSelection(next);
    const item = next.selectedItem;
    if (item) {
      if (!accountName && item.name) setAccountName(item.name);
      if (!accountCode) setAccountCode(item.codename);
    }
    if (errors.portfolio) {
      setErrors(prev => ({ ...prev, portfolio: null }));
    }
  };

  const handleDateChange = (value) => {
    setDate(value);
    if (errors.date) {
      setErrors(prev => ({ ...prev, date: null }));
    }
  };

  const validateForm = (sel = selection) => {
    const newErrors = {};

    if (!sel.selectedItem) {
      newErrors.portfolio = 'Portfolio is required';
    }

    if (!date) {
      newErrors.date = 'Date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async (submitted) => {
    // `submitted` is the Enter-resolved selection from PortfolioSelector (state
    // hasn't flushed yet); a button click passes a MouseEvent, so fall back to
    // state when it carries no selectedItem.
    const sel = (submitted && submitted.selectedItem) ? submitted : selection;
    if (!validateForm(sel)) {
      return;
    }

    setIsLoading(true);
    try {
      await onConfirm(buildParams(sel));
      onClose();
    } catch (error) {
      console.error('Report generation error:', error);
      setErrors({ submit: error.message || 'Failed to generate report' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`rounded-lg p-6 max-w-md w-full mx-4 shadow-xl ${
        darkMode ? 'bg-[#141414]' : 'bg-white'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {reportInfo?.name || 'Fixed Income Distribution'}
          </h3>
          <button
            onClick={onClose}
            className={`transition-colors ${
              darkMode
                ? 'text-gray-400 hover:text-gray-300'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Description */}
        {reportInfo?.description && (
          <div className={`mb-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {reportInfo.description}
          </div>
        )}

        {/* Form */}
        <div className="space-y-4">
          {/* Portfolio Selector */}
          <div>
            <PortfolioSelector darkMode={darkMode} prefill="" initialSelection={selectionFromParams(initialParams)} onChange={handleSelectionChange} onSubmit={handleConfirm}  disabled={isLoading}/>
            {errors.portfolio && (
              <p className="text-red-500 text-xs mt-1">{errors.portfolio}</p>
            )}
          </div>

          {/* Date Field */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Date <span className="text-red-500">*</span>
            </label>
            <MaskedDateField
              value={date}
              max={maxReportDate()}
              onChange={(e) => handleDateChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                darkMode
                  ? 'bg-[#1a1a1a] border-white/10 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } ${errors.date ? 'border-red-500' : ''}`}
              disabled={isLoading}
            />
            {errors.date && (
              <p className="text-red-500 text-xs mt-1">{errors.date}</p>
            )}
          </div>

          {/* Printed report-header inputs */}
          <div className="grid grid-cols-1 gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className={`block text-sm font-medium mb-1 ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Firm Name (printed at top of report)
              </label>
              <input
                type="text"
                value={firmName}
                onChange={(e) => setFirmName(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  darkMode
                    ? 'bg-[#1a1a1a] border-white/10 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                disabled={isLoading}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Account Name
                </label>
                <input
                  type="text"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="e.g. REDACTED-CLIENT"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    darkMode
                      ? 'bg-[#1a1a1a] border-white/10 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  disabled={isLoading}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Account Code
                </label>
                <input
                  type="text"
                  value={accountCode}
                  onChange={(e) => setAccountCode(e.target.value)}
                  placeholder="e.g. EQY002536"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    darkMode
                      ? 'bg-[#1a1a1a] border-white/10 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* Report Options */}
          <div className="space-y-2">
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Report Options
            </label>

            <div className="space-y-2">
              <label className={`flex items-center cursor-pointer ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <input
                  type="checkbox"
                  checked={showSummaryInformation}
                  onChange={(e) => setShowSummaryInformation(e.target.checked)}
                  className="mr-2 w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  disabled={isLoading}
                />
                <span className="text-sm">Show Summary Information</span>
              </label>

              <label className={`flex items-center cursor-pointer ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <input
                  type="checkbox"
                  checked={showDistributionByMoodyRating}
                  onChange={(e) => setShowDistributionByMoodyRating(e.target.checked)}
                  className="mr-2 w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  disabled={isLoading}
                />
                <span className="text-sm">Show Distribution by Moody Rating</span>
              </label>

              <label className={`flex items-center cursor-pointer ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <input
                  type="checkbox"
                  checked={showDistributionBySpRating}
                  onChange={(e) => setShowDistributionBySpRating(e.target.checked)}
                  className="mr-2 w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  disabled={isLoading}
                />
                <span className="text-sm">Show Distribution by S&P Rating</span>
              </label>

              <label className={`flex items-center cursor-pointer ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <input
                  type="checkbox"
                  checked={showDistributionByMaturity}
                  onChange={(e) => setShowDistributionByMaturity(e.target.checked)}
                  className="mr-2 w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  disabled={isLoading}
                />
                <span className="text-sm">Show Distribution by Maturity</span>
              </label>
            </div>
          </div>

          {errors.submit && (
            <div className={`p-3 rounded-lg border ${
              darkMode
                ? 'bg-red-900/20 border-red-800'
                : 'bg-red-50 border-red-200'
            }`}>
              <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                {errors.submit}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel || onClose}
            disabled={isLoading}
            className={darkMode ? 'border-gray-600 text-gray-300 hover:bg-[#1a1a1a]' : ''}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Generating...
              </>
            ) : (
              'OK'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FixedIncomeDistributionPopup;
